import { supabase, requireSession } from "@/lib/supabase/client";
import type { Json } from "@/lib/supabase/database.types";
import type { GeneratedWorkout } from "@/types";
import { migrateWorkouts, WORKOUT_SCHEMA_VERSION } from "@/lib/data/schemaVersions";
import { nextClientRevision, reliableWrite, rememberClientRevision } from "@/lib/persistence/reliableWrite";

type WorkoutPayload = {
  workouts: GeneratedWorkout[];
  isWeekLocked: boolean;
};

/** Returns the ISO date string (YYYY-MM-DD) of the Monday starting next week. */
export function getNextWeekStart(): string {
  const d = new Date();
  const day = d.getDay(); // 0=Sun … 6=Sat
  const daysUntilNextMonday = day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + daysUntilNextMonday);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Workout Service — reads and writes generated workouts to Supabase.
 *
 * The full workout array is stored as JSONB in user_workouts.data.
 * One row per user per week, keyed by (user_id, week_start_date).
 */
export const workoutService = {
  /**
   * Load the stored workouts for a specific week.
   * Returns null if no row exists yet for that week.
   */
  async load(userId: string, weekStartDate: string): Promise<WorkoutPayload | null> {
    const { data, error } = await supabase
      .from("user_workouts")
      .select("data, is_week_locked")
      .eq("user_id", userId)
      .eq("week_start_date", weekStartDate)
      .maybeSingle();

    if (error) {
      console.error("[workoutService.load] failed:", error.message);
      return null;
    }

    if (!data) return null;

    const workouts = migrateWorkouts(data.data);
    rememberClientRevision(`workouts:${userId}:${weekStartDate}`, Math.max(0, ...workouts.map((workout) => workout.revision ?? 0)));
    return {
      workouts,
      isWeekLocked: data.is_week_locked ?? false,
    };
  },

  /**
   * Save (upsert) workouts and lock state for a specific week.
   * Keyed by (user_id, week_start_date) so current and next week are stored separately.
   */
  async save(userId: string, weekStartDate: string, payload: WorkoutPayload): Promise<void> {
    await requireSession(userId);
    return reliableWrite(`workouts:${userId}:${weekStartDate}`, async () => {
    const key = `workouts:${userId}:${weekStartDate}`;
    const observed = Math.max(0, ...payload.workouts.map((workout) => workout.revision ?? 0));
    const revision = nextClientRevision(key, observed);
    const workouts = payload.workouts.map((workout) => ({ ...workout, schemaVersion: WORKOUT_SCHEMA_VERSION, revision }));
    const { data: accepted, error: rpcError } = await supabase.rpc("save_workouts_if_newer", {
      p_user_id: userId,
      p_week_start_date: weekStartDate,
      p_data: workouts as unknown as Json,
      p_is_week_locked: payload.isWeekLocked,
      p_revision: revision,
    });
    if (!rpcError && accepted === true) return;
    if (!rpcError && accepted === false) throw new Error("A newer weekly plan was saved on another device. Reload before trying again.");
    // Fall through during rolling migration; the existing RLS upsert remains safe.
    const { error } = await supabase.from("user_workouts").upsert(
      {
        user_id: userId,
        week_start_date: weekStartDate,
        data: workouts as unknown as Json,
        is_week_locked: payload.isWeekLocked,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id,week_start_date" },
    );

    if (error)
      throw new Error(
        `[workoutService.save] ${error.message} (code: ${error.code}; rpc: ${rpcError?.code ?? "empty-response"})${
          error.hint ? ` — ${error.hint}` : ""
        }`,
      );
    });
  },
};
